/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';

const features = [
  {
    id: 'feature-home',
    number: '01',
    image: '/assets/core-home-optimized.jpg',
    alt: 'Koemori ホーム画面',
    title: '見つかる、好きな歌声',
    description: 'シンプルで快適なフィードで、最新の投稿をチェック。あなたの好きな歌声と出会えます。',
  },
  {
    id: 'feature-sing',
    number: '02',
    image: '/assets/core-sing.webp',
    alt: 'Koemori 歌唱画面',
    title: '本格！精密採点機能',
    description: 'おなじみのバー表示で、音程をチェックしながら歌おう',
  },
  {
    id: 'feature-beautify',
    number: '03',
    image: '/assets/core-beautify.webp',
    alt: 'Koemori ボーカル美化画面',
    title: 'ワンタップ補正',
    description: '音程もリズムも、驚くほど自然に美しく。あなたの歌声をもっと魅力的に仕上げます。',
  },
  {
    id: 'feature-intimacy',
    number: '04',
    image: '/assets/core-intimacy.webp',
    alt: 'Koemori 親密関係画面',
    title: '歌声で、もっと近くなる',
    description: '歌声を通じて気持ちを分かち合い、音楽から生まれる親密なつながりを楽しめます。',
  },
];

function FeatureHeading() {
  return (
    <div className="w-full max-w-4xl mx-auto px-5 md:px-8 relative z-10 text-center mb-14 lg:mb-20">
      <motion.span
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        className="inline-block text-xs tracking-[0.28em] font-bold text-[#00F5D4] mb-4"
      >
        CORE FEATURES
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.55 }}
        className="text-3xl md:text-4xl font-black tracking-tight text-white mb-5"
      >
        主な機能
      </motion.h2>
    </div>
  );
}

export default function Features() {
  return (
    <div id="features" className="bg-[#0E1110]">
      {features.map((feature, index) => {
        const reverse = index % 2 === 1;
        return (
          <section key={feature.id} id={feature.id} className="feature-page min-h-screen py-20 lg:py-24 bg-[#0E1110] relative overflow-hidden flex flex-col justify-center">
            <div className={`absolute ${reverse ? '-left-32' : '-right-32'} top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#00F5D4]/5 blur-[130px] pointer-events-none`} />
            {index === 0 && <FeatureHeading />}
            <div className="w-full max-w-6xl mx-auto px-5 md:px-8 relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24 items-center">
              <motion.div
                initial={{ opacity: 0, x: reverse ? 34 : -34 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65 }}
                className={`flex justify-center ${reverse ? 'md:order-2' : 'md:order-1'}`}
              >
                <div className="relative rounded-[2rem] bg-black/25 border border-white/[0.08] p-3 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
                  <img src={feature.image} alt={feature.alt} width={1125} height={2436} loading="lazy" decoding="async" style={{ aspectRatio: '1125 / 2436' }} className="block w-auto max-w-full h-auto max-h-[56vh] rounded-[1.4rem] object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: reverse ? -34 : 34 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: 0.08 }}
                className={`${reverse ? 'md:order-1' : 'md:order-2'} max-w-xl`}
              >
                <div className="text-sm font-bold tracking-[0.2em] text-[#00F5D4] mb-5">{feature.number}</div>
                <h3 className="text-3xl md:text-4xl lg:text-3xl font-black tracking-tight text-white mb-5">{feature.title}</h3>
                <p className="text-base md:text-l text-white/55 leading-8">{feature.description}</p>
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
